<template>
  <div class="configurePageContainer">
    <div v-if="readPermission" class="configurePageContainer">
      <!-- Loading Spinner -->
      <div v-if="isLoading" class="loadingOverlay">
        <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
      </div>

      <!-- Title and Search -->
      <div class="customRowTenant">
        <p class="jobHistoryTitle" style="text-align: left">{{ $t('categories.title') }}</p>
        <div class="searchBar">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
            <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <input type="text" class="searchInput" :placeholder="$t('categories.search_placeholder')" v-model="search" @input="handleSearch" />
        </div>
      </div>

      <!-- Pagination and Actions -->
      <div class="tableHeadingsNew" style="margin-top: 20px">
        <div class="customRow">
          <CustomDropdown v-if="!loadingStates?.tenants" :label="$t('categories.select_tenant')" :placeholder="$t('categories.select_tenant')" v-model="selectedTenantId" :options="tenantsWithMaster" :disabled="userRole === 'Super Admin'" />
          <img src="/backward.svg" alt="Back" style="cursor: pointer" @click="goToPreviousPage" />
          <span class="pageOfTitle">{{ $t('common.page') }} {{ page }} {{ $t('common.of') }} {{ totalPages }}</span>
          <img src="/forward.svg" alt="Next" style="cursor: pointer" @click="goToNextPage" />
          <div class="pageDiv" @click.stop="togglePageSizeDropdown">
            <span class="pageDivText">{{ pageSize }}</span>
            <img src="/arrowDownNew.png" class="arrowDownNew" alt="Arrow Down" />
            <ul ref="dropdownEl" v-if="showPageSizeDropdown" class="pageSizeDropdownNew" @click.stop>
              <li v-for="size in pageSizeOptions" :key="size" @click.stop="selectPageSize(size)">
                {{ size }}
              </li>
            </ul>
          </div>
        </div>

        <div style="display: flex; gap: 17px">
          <ActiveFilter @filter-applied="handleFilterChange" ref="filterComponent" :initial-active="isActive === true ? 'Yes' : isActive === false ? 'No' : ''" />
          <SecondaryButton v-if="writePermission" @click="handleAddCategory">{{ $t('categories.add_button') }}</SecondaryButton>
        </div>
      </div>

      <!-- Data Table -->
      <div style="margin-top: 20px; overflow-y: auto">
        <table class="customTable" v-if="!loadingStates?.categories">
          <thead class="customTableHeader">
            <tr class="customTableRow">
              <th>{{ $t('categories.table.sr_no') }}</th>
              <th>{{ $t('categories.table.tenant_name') }}</th>
              <th>{{ $t('categories.table.primary_classification') }}</th>
              <th>{{ $t('categories.table.is_active') }}</th>
              <th v-if="writePermission">{{ $t('categories.table.action') }}</th>
            </tr>
          </thead>
          <tbody class="customTableBody">
            <tr v-if="!categories || categories.length === 0" style="height: 50px !important">
              <td colspan="5" style="text-align: center; color: #666">{{ $t('categories.no_data') }}</td>
            </tr>
            <tr v-else v-for="(category, index) in filteredCategories" :key="category.id" style="height: 50px !important">
              <td>{{ index + 1 + (page - 1) * pageSize }}</td>
              <td>{{ category?.tenant?.tenant_name || 'NA' }}</td>
              <td>{{ category.category_name || category?.category_display_name }}</td>
              <td>
                <div class="toggle-wrapper">
                  <CustomToggle :modelValue="category.is_active" @update:modelValue="(value) => handleStatusToggle(category.id, value)" :disabled="!writePermission" />
                </div>
              </td>
              <td v-if="readPermission">
                <CustomHyperlink @click="handleEditCategory(category)">{{ $t('categories.actions.edit') }}</CustomHyperlink>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Confirmation Modal -->
      <ConfirmationModal :show="showConfirmModal" :message="getConfirmationMessage()" @close="closeConfirmModal" @confirm="confirmStatusChange" />
    </div>
    <div v-else>
      <PermissionAlert :message="$t('common.no_permission')" />
    </div>
  </div>
</template>

<script>
import { get, put } from '@/utils/api'
import { showToast } from '@/utils/toast'

import CustomToggle from '@/components/CustomToggle/CustomToggle.vue'
import CustomHyperlink from '@/components/CustomHyperlink/CustomHyperlink.vue'
import SecondaryButton from '@/components/SecondaryButton/SecondaryButton.vue'
import ActiveFilter from '@/components/Filter/ActiveFilter.vue'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import CustomDropdown from '@/components/CustomDropdown/CustomDropdown.vue'
import PermissionAlert from '@/components/PermissionAlert.vue'
import tenantSelectionStore from '@/stores/tenantSelectionStore'

const SESSION_KEY_FILTERS = 'primaryClassificationFilters'

export default {
  components: {
    CustomToggle,
    CustomHyperlink,
    SecondaryButton,
    ActiveFilter,
    ConfirmationModal,
    CustomDropdown,
    PermissionAlert,
  },

  data() {
    let storedFilters = {}
    try {
      storedFilters = JSON.parse(sessionStorage.getItem(SESSION_KEY_FILTERS)) || {}
    } catch (_) {
      storedFilters = {}
    }
    return {
      readPermission: false,
      writePermission: false,
      readPermissionMaster: false,
      userRole: localStorage.getItem('userRole'),
      writePermissionMaster: false,
      loading: false,
      loadingStates: {
        categories: false,
        tenants: false,
      },

      categories: [],
      tenants: [],
      tenantsWithMaster: [],
      tenantStoreUnsubscribe: null,

      selectedTenantId: '',
      search: storedFilters.search || '',
      page: storedFilters.page || 1,
      pageSize: storedFilters.pageSize || 10,
      total: storedFilters.total || 0,
      totalPages: storedFilters.totalPages || 1,
      pageSizeOptions: [10, 20, 50, 100],

      searchTenantId: null,

      showPageSizeDropdown: false,
      showConfirmModal: false,

      pendingStatusChange: null,
      selectedCategory: null,

      debounceTimeout: null,
      isActive: storedFilters.isActive !== undefined ? storedFilters.isActive : null,
    }
  },

  async mounted() {
    console.log('🚨 CategoriesGrid: MOUNTED METHOD CALLED - START')
    document.title = this.$t('categories.title')
    this.checkPermissions()

    // Get current state from store
    console.log('🏠 CategoriesGrid: Getting current state from store')
    const storeState = tenantSelectionStore.getCurrentState()

    this._isStoreUpdate = true
    this.selectedTenantId = storeState.selectedTenantId
    this._isStoreUpdate = false

    // Subscribe to tenant selection store
    this.tenantStoreUnsubscribe = tenantSelectionStore.subscribe((state) => {
      const previousTenantId = this.selectedTenantId
      console.log('📡 CategoriesGrid: Store update received')
      console.log('  - New state:', state)
      console.log('  - Current component tenant ID:', this.selectedTenantId)
      console.log('  - Previous tenant ID:', previousTenantId)

      // Set flag to prevent watcher from triggering store update
      this._isStoreUpdate = true
      this.selectedTenantId = state.selectedTenantId
      this._isStoreUpdate = false

      console.log('🔄 CategoriesGrid: Updated selectedTenantId to:', this.selectedTenantId)

      // Only fetch data if tenant actually changed and it's not the initial load
      if (previousTenantId !== state.selectedTenantId && previousTenantId !== '') {
        console.log('📋 CategoriesGrid: Tenant changed from', previousTenantId, 'to', state.selectedTenantId, '- fetching new data')
        this.fetchCategories()
      } else {
        console.log('🚀 CategoriesGrid: Tenant unchanged or initial load, skipping data fetch')
      }
    })

    // First fetch tenants to ensure selectedTenantId is properly set
    await this.fetchTenants()
    // Then fetch categories with the correct tenant ID
    await this.fetchCategories()
    this.saveFiltersToSession()
    document.addEventListener('click', this.handleClickAway)

    console.log('🚨 CategoriesGrid: MOUNTED METHOD CALLED - END')
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
    // Unsubscribe from tenant selection store
    if (this.tenantStoreUnsubscribe) {
      this.tenantStoreUnsubscribe()
    }
  },

  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      // const isMasterTenant = this.selectedTenantId === 'master'

      // Check for master tenant permissions
      this.readPermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_RD' && p.is_active)
      this.writePermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_WR' && p.is_active)

      // Check for tenant permissions
      this.readPermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_WR' && p.is_active)
    },
    saveFiltersToSession() {
      const filters = {
        tenantId: this.selectedTenantId,
        search: this.search,
        page: this.page,
        pageSize: this.pageSize,
        total: this.total,
        totalPages: this.totalPages,
        isActive: this.isActive,
      }
      sessionStorage.setItem(SESSION_KEY_FILTERS, JSON.stringify(filters))
    },
    handleClickAway(event) {
      const dropdown = this.$refs.dropdownEl
      if (dropdown && !dropdown.contains(event.target)) {
        this.showPageSizeDropdown = false
      }
    },

    async fetchCategories() {
      this.loadingStates.categories = true
      try {
        let url = this.selectedTenantId && this.selectedTenantId !== 'master' ? `/v1/tenant-categories?page=${this.page}&pageSize=${this.pageSize}&tenantId=${this.selectedTenantId}` : `/v1/categories?page=${this.page}&pageSize=${this.pageSize}`

        if (this.search) {
          url += `&search=${encodeURIComponent(this.search)}`
        }

        if (this.isActive !== null) {
          url += `&isActive=${this.isActive}`
        }

        const { data } = await get(url)

        if (data?.code === 200) {
          const { data: categoryData, total } = data.data
          this.categories = categoryData
          this.total = total
          this.totalPages = Math.ceil(total / this.pageSize)
        }
      } catch (err) {
        showToast({
          title: 'Error',
          message: 'Failed to fetch primary classifications',
          type: 'error',
        })
      } finally {
        this.loadingStates.categories = false
      }
    },

    async fetchTenants() {
      this.loadingStates.tenants = true
      try {
        const { data } = await get('/v1/tenants?page=1&pageSize=10000')

        if (data?.code === 200) {
          this.tenants = (data.data.tenants || []).map((t) => ({
            label: t.tenant_name,
            value: t.id,
          }))
          if (this.readPermissionMaster) {
            if (localStorage.getItem('userRole') === 'Super Admin' || localStorage.getItem('userRole') === 'Admin') {
              this.tenantsWithMaster = [{ label: this.$t('categories.forms.options.master_tenant'), value: 'master' }, ...this.tenants]
            } else {
              this.tenantsWithMaster = this.tenants
              this.selectedTenantId = this?.tenants?.[0]?.value || 'master'
            }
          } else {
            this.tenantsWithMaster = this.tenants
            this.selectedTenantId = this?.tenants?.[0]?.value || 'master'
          }
        }
      } catch (err) {
      } finally {
        this.loadingStates.tenants = false
      }
    },

    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },

    async selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      await this.fetchCategories()
      this.saveFiltersToSession()
    },

    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchCategories()
        this.saveFiltersToSession()
      }
    },

    async goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        await this.fetchCategories()
        this.saveFiltersToSession()
      }
    },

    handleSearch() {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.page = 1
        this.fetchCategories()
      }, 300)
    },

    handleFilterChange(filters) {
      this.isActive = filters.active === 'Yes' ? true : filters.active === 'No' ? false : null
      this.page = 1
      this.fetchCategories()
      this.saveFiltersToSession()
    },

    handleStatusToggle(categoryId, value) {
      this.selectedCategory = this.categories.find((c) => c.id === categoryId)
      this.pendingStatusChange = { categoryId, value }

      this.showConfirmModal = true
    },

    closeConfirmModal() {
      this.showConfirmModal = false
      this.pendingStatusChange = null
    },

    async confirmStatusChange() {
      if (!this.pendingStatusChange) return

      const { categoryId, value } = this.pendingStatusChange
      await this.updateCategoryStatus(categoryId, value)
      this.closeConfirmModal()
    },

    async updateCategoryStatus(categoryId, value) {
      try {
        const category = this.categories.find((c) => c.id === categoryId)
        if (!category) return

        const url = this.selectedTenantId && this.selectedTenantId !== 'master' ? `/v1/tenant-categories/${categoryId}` : `/v1/categories/${categoryId}`

        const payload = {
          ...category,
          is_active: value,
        }

        const { data } = await put(url, payload)

        if (data?.code === 200) {
          category.is_active = value
          showToast({
            title: 'Success',
            message: `Primary Classification ${value ? 'enabled' : 'disabled'} successfully`,
            type: 'success',
          })
        }
      } catch (error) {
        showToast({
          title: 'Error',
          message: 'Failed to update Primary Classification status',
          type: 'error',
        })
      }
    },

    async handleTenantChangeSearch(tenantId) {
      this.searchTenantId = tenantId
      this.page = 1
      await this.fetchCategories()
    },

    handleIsActiveChange(value) {
      this.newCategory.is_active = value
    },

    handleEditCategory(category) {
      if (!category || !category.id) {
        return
      }
      // Determine if it's a tenant or master category
      const isMaster = !category.tenant_id

      this.$router.push({
        name: 'CategoryDetails',
        params: { id: category.id },
        query: { tenantId: isMaster ? 'master' : category.tenant_id },
      })
    },
    handleAddCategory() {
      const isMaster = !this.selectedTenantId

      this.$router.push({
        name: 'CategoryDetails',
        params: { id: 'add' },
        query: { tenantId: isMaster ? 'master' : this.selectedTenantId },
      })
    },
    handleViewDocument(detail) {
      const tenantName = localStorage.getItem('url_name')
      this.$router.push({
        name: 'view-document',
        params: {
          id: detail.id,
          tenantName,
        },
      })
    },
    handleEditDocument(detail) {
      const tenantName = localStorage.getItem('url_name')
      this.$router.push({
        name: 'edit-document',
        params: {
          id: detail.id,
          tenantName,
        },
      })
    },
    getConfirmationMessage() {
      if (!this.selectedCategory) return ''
      const categoryName = this.selectedCategory.category_name || this.selectedCategory.category_display_name
      const messageKey = this.selectedCategory.is_active ? 'categories.modals.confirm_status.disable_message' : 'categories.modals.confirm_status.enable_message'
      return this.$t(messageKey, { categoryName })
    },
  },
  computed: {
    filteredCategories() {
      console.log('🔄 CategoriesGrid: filteredCategories computed called at', new Date().toLocaleTimeString())
      if (!this.categories || this.categories.length === 0) {
        console.log('📋 CategoriesGrid: No categories to filter')
        return []
      }

      // Always filter out Bedrock Training and Bedrock Documents categories
      const filtered = this.categories.filter((category) => {
        const categoryName = category.category_name || category.category_display_name || ''
        const shouldInclude = categoryName !== 'Bedrock Training' && categoryName !== 'Bedrock Documents' && categoryName !== 'Textract Training'
        if (!shouldInclude) {
          console.log('📋 CategoriesGrid: \u274c Filtering out category:', categoryName)
        } else {
          console.log('📋 CategoriesGrid: \u2705 Including category:', categoryName)
        }
        return shouldInclude
      })
      return filtered
    },
    isLoading() {
      return Object.values(this.loadingStates).some((state) => state)
    },
  },
  watch: {
    selectedTenantId(newValue, oldValue) {
      // Only react if this is a manual change (not from store subscription)
      if (newValue === oldValue || this._isStoreUpdate) return

      console.log('👀 CategoriesGrid: selectedTenantId watcher triggered (manual change):', newValue, 'from:', oldValue)

      this.page = 1
      this.activeFilters = null
      this.checkPermissions()
      this.fetchCategories()
      this.saveFiltersToSession()
    },
    search() {
      this.saveFiltersToSession()
    },
    page() {
      this.saveFiltersToSession()
    },
    pageSize() {
      this.saveFiltersToSession()
    },
    total() {
      this.saveFiltersToSession()
    },
    totalPages() {
      this.saveFiltersToSession()
    },
  },
}
</script>

<style scoped>
.searchBar {
  display: flex;
  align-items: center;
  width: 386px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dadada;
  background: #fff;
  padding: 0 10px;
  gap: 10px;
}

.searchInput {
  border: none;
  outline: none;
  width: 100%;
  font-family: Archivo;
  font-size: 14px;
  font-weight: 400;
  color: #8f969b;
}

.pageSizeDropdownNew {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  list-style: none;
  padding: 4px 0;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pageSizeDropdownNew li {
  padding: 6px 12px;
  cursor: pointer;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #5c656c;
}

.pageSizeDropdownNew li:hover {
  background: #f0f0f0;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.loadingOverlay {
  position: absolute;
  top: 50%;
  left: 65%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
